// jwt.interceptor.ts
import { HttpEvent, HttpHandlerFn, HttpRequest, HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthenticatorService } from '@aws-amplify/ui-angular';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Observable, from, throwError, of } from 'rxjs';
import { catchError, map, switchMap, retry, tap } from 'rxjs/operators';
import { CsrfService } from '../services/csrf.service';

export const JwtInterceptor: HttpInterceptorFn = (request: HttpRequest<unknown>, next: HttpHandlerFn) => {
    const authenticatorService = inject(AuthenticatorService);
    const csrfService = inject(CsrfService);

    return from(fetchAuthSession()).pipe(
        switchMap(ses => {
            if (!request.headers.has('Content-Type') ) {
                request = request.clone({
                    headers: request.headers.set('Content-Type', 'application/json')
                });
            }

            const token = ses.tokens?.idToken?.toString();
            let clonedRequest = request.clone({
                headers: request.headers.set('Authorization', `Bearer ${token}`)
            });

            clonedRequest = clonedRequest.clone({
                headers: clonedRequest.headers.set('Accept', 'application/json')
            });

            // Add CSRF token for ALL requests (including GET) since GET endpoints now require CSRF
            // Skip CSRF for the token generation endpoint itself to avoid infinite loops
            if (!request.url.includes('/csrf-token')) {
                const csrfToken = csrfService.getCurrentToken();
                if (csrfToken) {
                    clonedRequest = clonedRequest.clone({
                        headers: clonedRequest.headers.set('X-CSRF-Token', csrfToken)
                    });
                    
                    // Add session ID for anonymous requests (when no JWT token)
                    if (!token) {
                        const sessionId = csrfService.getSessionId();
                        if (sessionId) {
                            clonedRequest = clonedRequest.clone({
                                headers: clonedRequest.headers.set('X-Session-Id', sessionId)
                            });
                            //console.log('📋 [JWT_INTERCEPTOR] Added session ID for anonymous request');
                        }
                    }
                } else {
                    // No CSRF token available - we'll handle this in the error handling
                    //console.warn('⚠️ [JWT_INTERCEPTOR] No CSRF token available, request may fail');
                }
            }

            if (request.url.indexOf('s3.us-east-2.') < 0) {
                return next(clonedRequest).pipe(
                    map((event: HttpEvent<any>) => {
                        return event;
                    }),
                    catchError((error: HttpErrorResponse) => {
                        console.log(error);
                        if (error.status === 401) {
                            // Handle 401 error
                        } else if (error.status === 403 && isCSRFError(error)) {
                            // CSRF token missing, expired or invalid, refresh and retry                            
                            return refreshCSRFTokenAndRetry(clonedRequest, next, csrfService);
                        }
                        return throwError(() => error);
                    })
                );
            } else {
                return next(request).pipe(
                    map((event: HttpEvent<any>) => {
                        return event;
                    }),
                    catchError((error: HttpErrorResponse) => {
                        console.log(error);
                        if (error.status === 401) {
                            // Handle 401 error
                        }
                        return throwError(() => error);
                    })
                );
            }
        })
    );
};


/**
 * Check if the error is a CSRF-related error
 */
function isCSRFError(error: HttpErrorResponse): boolean {
    return error.error?.csrfError === true ||
           error.error?.error === 'CSRF Token Missing' ||
           error.error?.error === 'CSRF Token Invalid';
}

/**
 * Refresh CSRF token and retry the request
 */
function refreshCSRFTokenAndRetry(request: HttpRequest<any>, next: HttpHandlerFn, csrfService: CsrfService): Observable<HttpEvent<any>> {
    // Call the CSRF token endpoint
    return from(csrfService.fetchToken()).pipe(
        switchMap((csrfToken) => {
            if (csrfToken) {
                // Update the request with the new CSRF token
                const updatedRequest = request.clone({
                    headers: request.headers.set('X-CSRF-Token', csrfToken)
                });
                return next(updatedRequest);
            } else {
                return throwError(() => new Error('Failed to refresh CSRF token'));
            }
        }),
        catchError((error) => {
            //console.error('CSRF token refresh failed:', error);
            return throwError(() => error);
        })
    );
}

